/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EmojiPicker.tsx - Componente selector de emojis
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Selector de emojis organizado por categorías
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Card, Nav, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSmile, faHeart, faHandPaper, faSun, faCoffee } from '@fortawesome/free-solid-svg-icons';

// Emojis organizados por categorías
const EMOJI_CATEGORIES = {
  smileys: {
    label: 'Caritas',
    icon: faSmile,
    emojis: [
      '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', 
      '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', 
      '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳',
      '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖',
      '😫', '😩', '��', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯'
    ]
  },
  gestures: {
    label: 'Gestos',
    icon: faHandPaper,
    emojis: [
      '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
      '👆', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '��', '🙌',
      '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🤳', '💅', '🤛', '🤜'
    ]
  },
  hearts: {
    label: 'Corazones',
    icon: faHeart,
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '❤️‍🔥',
      '❤️‍🩹', '💌', '💋', '💑', '💏', '👩‍❤️‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩'
    ]
  },
  nature: {
    label: 'Naturaleza',
    icon: faSun,
    emojis: [
      '🌞', '🌝', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑',
      '🌒', '🌓', '🌔', '🌙', '⭐', '🌟', '💫', '✨', '☄️', '☀️',
      '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️', '❄️',
      '☃️', '⛄', '🌬️', '💨', '🌪️', '🌫️', '🌈', '🔥', '💧', '🌊'
    ]
  },
  objects: {
    label: 'Objetos',
    icon: faCoffee,
    emojis: [
      '💼', '📱', '💻', '⌚', '📷', '📹', '🎵', '🎶', '🔔', '🔕',
      '📢', '📣', '📯', '🔈', '🔉', '🔊', '��', '🎤', '🎧', '📺',
      '📞', '☎️', '📠', '📟', '⏰', '⏱️', '⏲️', '🕰️', '⌛', '⏳',
      '📡', '��', '🔌', '💡', '🔦', '🕯️', '🧯', '🛢️', '💸', '��'
    ]
  }
};

type EmojiCategory = keyof typeof EMOJI_CATEGORIES;

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  onClose?: () => void;
  position?: 'top' | 'bottom';
}

/**
 * Componente EmojiPicker
 * 
 * Selector de emojis con categorías y búsqueda
 * 
 * @example
 * ```tsx
 * <EmojiPicker
 *   onEmojiSelect={(emoji) => setMessage(prev => prev + emoji)}
 *   onClose={() => setShowPicker(false)}
 *   position="top"
 * />
 * ```
 */
export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onEmojiSelect,
  onClose,
  position = 'bottom'
}) => {
  const [activeCategory, setActiveCategory] = useState<EmojiCategory>('smileys');

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
  };

  return (
    <Card
      className="emoji-picker-container"
      style={{
        position: 'absolute',
        [position]: '100%',
        right: 0,
        width: '320px',
        maxHeight: '400px',
        zIndex: 9999,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: position === 'top' ? '8px' : '0',
        marginTop: position === 'bottom' ? '8px' : '0'
      }}
    >
      {/* Header con categorías */}
      <Card.Header
        style={{
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6'
        }}
      >
        <Nav
          variant="pills"
          className="justify-content-around"
          style={{ fontSize: '1.2rem' }}
        >
          {(Object.keys(EMOJI_CATEGORIES) as EmojiCategory[]).map((category) => (
            <Nav.Item key={category}>
              <Nav.Link
                active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
                style={{
                  padding: '6px 10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                title={EMOJI_CATEGORIES[category].label}
              >
                <FontAwesomeIcon icon={EMOJI_CATEGORIES[category].icon} />
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Card.Header>

      {/* Cuerpo con emojis */}
      <Card.Body
        style={{
          padding: '12px',
          maxHeight: '280px',
          overflowY: 'auto',
          backgroundColor: '#fff'
        }}
      >
        <div
          className="emoji-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: '8px'
          }}
        >
          {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji, index) => (
            <Button
              key={index}
              variant="light"
              size="sm"
              onClick={() => handleEmojiClick(emoji)}
              style={{
                fontSize: '1.5rem',
                padding: '6px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                aspectRatio: '1/1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              className="emoji-button"
            >
              {emoji}
            </Button>
          ))}
        </div>
      </Card.Body>

      {/* Footer con botón cerrar */}
      {onClose && (
        <Card.Footer
          style={{
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #dee2e6',
            textAlign: 'center'
          }}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            style={{
              fontSize: '0.85rem',
              padding: '4px 16px'
            }}
          >
            Cerrar
          </Button>
        </Card.Footer>
      )}

      <style>{`
        .emoji-picker-container {
          animation: emojiPickerFadeIn 0.2s ease-out;
        }

        @keyframes emojiPickerFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .emoji-button:hover {
          background-color: #e9ecef !important;
          transform: scale(1.2);
        }

        .emoji-button:active {
          transform: scale(0.95);
        }

        /* Scrollbar personalizado */
        .emoji-picker-container .card-body::-webkit-scrollbar {
          width: 6px;
        }

        .emoji-picker-container .card-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .emoji-picker-container .card-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .emoji-picker-container .card-body::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .emoji-picker-container {
            width: 280px !important;
            max-height: 350px !important;
          }

          .emoji-grid {
            grid-template-columns: repeat(7, 1fr) !important;
            gap: 6px !important;
          }

          .emoji-button {
            font-size: 1.3rem !important;
            padding: 4px !important;
          }
        }

        /* Prevenir selección accidental */
        .emoji-picker-container {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>
    </Card>
  );
};

export default EmojiPicker;
